import Button from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import { convertFirebaseTimestampToDate } from '@creator/ui/utils/date-utils';
import AlertBox from '@src/basic-components/AlertBox/AlertBox';
import DraftPostSelectionModal from '@src/components/DraftPostSelectionModal/DraftPostSelectionModal';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { useStoreState } from '@src/model/hooks';
import { PostStatus } from '@src/store/contentEntities/model';
import { usePost } from '@src/store/posts/hooks/usePost';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { memo, useContext } from 'react';
import { useTranslation } from 'react-i18next';

interface PostStatusIndicatorProps {
    postId: string;
    tokenName: string;
    variant?: 'text' | 'alert-box';
}

const PostStatusIndicator: React.FC<PostStatusIndicatorProps> = props => {
    const { postId } = props;
    const post = usePost(postId);
    const { t } = useTranslation();

    if (!post) return null;

    function getDetailsText() {
        if (!post) return '';
        const { createdAt, status, scheduledAt } = post;

        if (status === PostStatus.PUBLISHED_SCHEDULED && scheduledAt)
            return `${convertFirebaseTimestampToDate(scheduledAt).toUTCString()} / ${convertFirebaseTimestampToDate(scheduledAt).toTimeString()}`;
        if (status === PostStatus.PUBLISHED && createdAt) return convertFirebaseTimestampToDate(createdAt).toUTCString();
    }

    function getText(): string {
        if (!post) return '';
        return `${t(`postStatusIndicator.status${post.status}`, { details: getDetailsText() })}`;
    }

    return (
        <AlertBox type="warning" className="flex items-center justify-between border-none">
            <Text enableMultiline={true} className="">
                {getText()}
            </Text>
        </AlertBox>
    );
};

export default memo(PostStatusIndicator, propsEqualityFn);
